import React from "react";
import {Column, HeaderGroup, useSortBy, useTable} from "react-table";
import {useAsync} from "react-async-hook";
import { Pagination } from "./Pagination";
import {NoDataMessage} from "./NoDataMessage";

interface ListTableProps<D extends object> {
  tableData: D[];
  columns: Column<D>[];
  onSort: (sortBy: string[]) => Promise<void>;
  initialSort: string[] // 「＜カラム名＞:＜ソート順（asc or desc）＞」の形式
  totalCount: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (selectedItem: { selected: number }) => Promise<void>;
  onPageSizeChange: (newPageSize: number) => Promise<void>;
}

interface SortDirectionIndicatorProps<D extends object> {
  column: HeaderGroup<D>
}

const SortDirectionIndicator = <D extends object>(props: SortDirectionIndicatorProps<D>) => {
  const { column } = props;

  return (
    <>
        <span>
          {
            (column.isSorted && column.isSortedDesc)
              ? '▼' // ToDo: アイコンに置き換え
              : ''
          }

          {
            (column.isSorted && !column.isSortedDesc)
              ? '▲' // ToDo: アイコンに置き換え
              : ''
          }
        </span>
    </>
  )
}

export const ListTable = <DataType extends object>(props: ListTableProps<DataType>) => {
  const {
    tableData,
    columns,
    onSort,
    initialSort,
    totalCount,
    pageSize,
    currentPage,
    onPageChange,
    onPageSizeChange,
  } = props;

  const tableInstance = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: initialSort.map((sort) => {
          const sortItems = sort.split(':');
          return {
            id: sortItems[0],
            desc: sortItems[1] === 'desc',
          }
        })
      },
      disableSortRemove: true,
      manualSortBy: true,
      autoResetSortBy: false,
    },
    useSortBy
  );


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state: { sortBy }
  } = tableInstance;

  useAsync(async () => {
    onSort([`${sortBy[0].id}:${sortBy[0].desc ? 'desc' : 'asc'}`]);
  }, [sortBy]);

  return (
    <>
      <div className="flex justify-end mb-2">
        <Pagination
          {...{
            totalCount,
            pageSize,
            currentPage,
            onPageChange,
            onPageSizeChange,
          }}
        />
      </div>
      <div className="overflow-x-scroll whitespace-nowrap block">
        { /* eslint-disable-next-line react/jsx-props-no-spreading */ }
        <table {...getTableProps({
          className: "table-auto border-separate w-full"
        })}>
          <thead className="bg-primary text-white text-xs w-full">
          {
            headerGroups.map(headerGroup => (
              // eslint-disable-next-line react/jsx-props-no-spreading,react/jsx-key
              <tr {...headerGroup.getHeaderGroupProps()}>
                {
                  headerGroup.headers.map(column => (
                    // eslint-disable-next-line react/jsx-props-no-spreading,react/jsx-key
                    <th className='py-1 px-2 select-none relative' {...column.getHeaderProps(column.getSortByToggleProps())}>
                      <span>{column.render('Header')}</span>
                      <span className="absolute right-0 mr-1">
                          <SortDirectionIndicator column={column} />
                        </span>
                    </th>
                  ))}
              </tr>
            ))}
          </thead>
          { /* eslint-disable-next-line react/jsx-props-no-spreading */ }
          <tbody {...getTableBodyProps({
            className: "bg-white text-sm"
          })}>
          {
            rows.map(row => {
              prepareRow(row)
              return (
                // eslint-disable-next-line react/jsx-props-no-spreading,react/jsx-key
                <tr {...row.getRowProps()}>
                  {
                    row.cells.map(cell => {
                      return (
                        // eslint-disable-next-line react/jsx-props-no-spreading,react/jsx-key
                        <td {...cell.getCellProps({
                          className: "p-4 font-medium whitespace-normal"
                        })} >
                          {// Render the cell contents
                            cell.render('Cell')}
                        </td>
                      )
                    })}
                </tr>
              )
            })}
          </tbody>
        </table>
        { tableData.length === 0 && (
          <div className="mt-6">
            <NoDataMessage />
          </div>
        )}
      </div>
    </>
  );
};
